import { FC, useEffect, useMemo, useState } from 'react';
import { Dialog, Grid, Tooltip } from '@mui/material';
import MDTypography from '@components/MDTypography';
import { FaIcon, MDBox, MDButton, MDInput } from '@components';
import { ADD_CHART_PLACEHOLDER, ERROR_STATUS_500 } from '@constants/messages';
import { useSendSquirrelBotRequestMutation } from '@api/projects';
import brandLogo from '@assets/images/DS_Logo.png';
import useCustomNavigate from '@hooks/useCustomNavigate';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  REMOVED_VOTE,
  CLICKED_SQBOT_DOWNVOTE,
  CLICKED_SQBOT_UPVOTE,
  INPUT_SQUIRREL_BOT,
  USED_SQUIRBY
} from '@constants/eventNames';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { setSqBotData } from '@pages/ProjectBoard/slice';
import { containsWhiteSpace } from '@utils/common';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { FeatureTypes } from '@constants/common';
import UserAuth from '@services/auth';
import useUserDetails from '@hooks/useUserDetails';

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  chartId?: string;
  setRefetchChartConfigs?: React.Dispatch<React.SetStateAction<boolean>>;
  initText?: string;
  setStartPollingForSquirby?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SqBChatWidget: FC<Props> = ({
  open,
  onClose,
  projectId,
  chartId,
  setRefetchChartConfigs,
  initText = '',
  setStartPollingForSquirby
}) => {
  const dispatch = useDispatch();

  const sqBotData = useSelector(
    (state: RootState) => state?.projectBoard?.sqBotData
  );
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const isSquirbyEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.squirby
  );

  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const currentPlanFeatures = useSelector(
    (state: RootState) => state?.AppData.currentPlanFeatures
  );
  const user = UserAuth.userInfo;

  const { getUserDetails } = useUserDetails();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const [botSearchText, setBotSearchText] = useState(
    initText || sqBotData?.draftPrompt || ''
  );
  const [clickedSeeMore, setClickedSeeMore] = useState(false);

  const [botRequestData, setBotRequestData] = useState({
    message: sqBotData.message || '',
    sqb_id: sqBotData.sqb_id || '',
    chart_id: sqBotData.chart_id || '',
    action: sqBotData.action || '',
    button_text: sqBotData.button_text || '',
    prompt: sqBotData.prompt || '',
    rating: sqBotData.rating || 0
  });

  const [usedButton, setUsedButton] = useState(sqBotData.usedButton);
  const navigate = useCustomNavigate();

  const [
    sendSquirrelBotRequest,
    { isLoading: botReqLoading, error: botReqError, isSuccess: botReqSuccess }
  ] = useSendSquirrelBotRequestMutation();

  const [sendVoteValue] = useSendSquirrelBotRequestMutation();

  const sendSqBRequest = async (prompt?: string) => {
    const response = await sendSquirrelBotRequest({
      project_id: projectId,
      prompt_input: prompt || botSearchText,
      ...(chartId && { chart_id: chartId })
    });

    if ('data' in response) {
      setBotSearchText('');
      setBotRequestData({ ...response.data, prompt: prompt || botSearchText });
      dispatch(
        setSqBotData({
          ...response.data,
          prompt: prompt || botSearchText,
          draftPrompt: ''
        })
      );
      sendAmplitudeData(USED_SQUIRBY, {
        Status: 'success',
        response_code: response?.data?.response_code,
        chart_id: response?.data?.chart_id,
        action: response?.data?.action,
        ...(response?.data?.suggested_chart &&
          response?.data?.suggested_chart?.x_type && {
            'Default Data Type combination': `${response?.data?.suggested_chart?.x_type} & ${response?.data?.suggested_chart?.y_type}`
          })
      });
    } else {
      if (response?.error?.status === 402) return;
      setBotSearchText('');
      sendAmplitudeData(USED_SQUIRBY, {
        Status: 'fail',
        response_code: botReqError?.status || botReqError?.originalStatus
      });
      setBotRequestData({
        message: ERROR_STATUS_500,
        sqb_id: '',
        chart_id: '',
        action: '',
        button_text: '',
        prompt: prompt || botSearchText,
        rating: 0
      });
      dispatch(
        setSqBotData({
          message: ERROR_STATUS_500,
          sqb_id: '',
          chart_id: '',
          action: '',
          button_text: '',
          prompt: prompt || botSearchText,
          draftPrompt: ''
        })
      );
    }
  };

  const handleSquirrelBotReq = (
    event?: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      (event ? event?.key === 'Enter' : true) &&
      botSearchText.length !== 0 &&
      !botReqLoading
    ) {
      if (event) event?.preventDefault();
      sendAmplitudeData(INPUT_SQUIRREL_BOT, { location_type: 'Floating SqB' });
      setBotRequestData({
        message: '',
        sqb_id: '',
        chart_id: '',
        action: '',
        button_text: '',
        prompt: botSearchText,
        rating: 0
      });
      setClickedSeeMore(false);
      sendSqBRequest();
    }
  };

  useEffect(() => {
    if (initText) sendSqBRequest(initText);
  }, [initText]);

  const handleVoteClick = (rating: number) => {
    const newRating = rating === botRequestData?.rating ? 0 : rating;

    if (newRating === 1)
      sendAmplitudeData(CLICKED_SQBOT_UPVOTE, {
        action: botRequestData?.action
      });
    else if (newRating === -1)
      sendAmplitudeData(CLICKED_SQBOT_DOWNVOTE, {
        action: botRequestData?.action
      });
    else
      sendAmplitudeData(REMOVED_VOTE, {
        Rating: rating == 1 ? 'upvote' : 'downvote',
        Type: 'SqB'
      });

    setBotRequestData((prev) => ({ ...prev, rating: newRating }));
    dispatch(setSqBotData({ rating: newRating }));
    sendVoteValue({
      project_id: projectId,
      prompt_input: botRequestData?.prompt,
      rating: newRating,
      sqb_id: botRequestData?.sqb_id
    });
  };

  const handleButtonClick = (action: string) => {
    setUsedButton(true);
    dispatch(setSqBotData({ usedButton: true }));
    if (action === 'refresh' && setRefetchChartConfigs && chartId)
      setRefetchChartConfigs(true);

    if (action === 'redirect' && botRequestData?.chart_id)
      navigate(`/chart/${botRequestData?.chart_id}/edit`);
  };

  useEffect(() => {
    if (
      botRequestData?.action === 'refresh' &&
      setRefetchChartConfigs &&
      !usedButton &&
      !chartId &&
      setStartPollingForSquirby
    )
      setStartPollingForSquirby(true);

  }, [botRequestData?.action]);

  const handleSquriByLockedOnChange = async () => {
    if (subscriptionFlag) getFeatureAccess(FeatureTypes.SQUIRBY);
  };

  useEffect(() => {
    const userId = user?.id;

    if (UserAuth.isAuthenticated && userId) getUserDetails(userId as string);
  }, [botReqError, botReqSuccess]);

  const remainingCredits = useMemo(() => {
    const squirbyLimit = currentPlanFeatures?.squirbyLimit;

    const squirbyLimitsLeft = squirbyLimit
      ? `${userDetails?.squirby_limit_remaining || 0}/${squirbyLimit}`
      : '';

    return squirbyLimitsLeft;
  }, [userDetails, currentPlanFeatures]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ zIndex: 5000 }}
      PaperProps={{
        sx: {
          overflow: 'visible',
          position: 'absolute',
          paddingX: '15px',
          pt: '15px',
          pb: '5px',
          bottom: -5,
          right: -20,
          width: '400px',
          height: '74vh',
          boxShadow: '0 3px 25px rgb(0 0 0 / 0.2)'
        }
      }}
    >
      <>
        <MDButton
          size='small'
          color='white'
          sx={({ palette }) => ({
            position: 'absolute',
            left: '-40px',
            top: '0px',
            background: palette.primary.main,
            borderRadius: '50%',
            height: '20px',
            width: '20px',
            minWidth: 'unset',
            border: 'none',
            zIndex: 100,
            '&:hover': { backgroundColor: palette.primary.main }
          })}
          variant='contained'
          disableRipple
          onClick={() => onClose()}
        >
          <FaIcon
            typographyProps={{ variant: 'sm', color: 'white' }}
            icon='xmark'
          />
        </MDButton>
        <Tooltip
          title={remainingCredits + ' remaining'}
          PopperProps={{ sx: { zIndex: 6000 } }}
        >
          <MDTypography
            variant='caption'
            sx={{ position: 'absolute', top: 10, right: 15 }}
          >
            {remainingCredits}
          </MDTypography>
        </Tooltip>
        <MDBox
          display={'flex'}
          justifyContent='space-between'
          flexDirection='column'
          height='100%'
          overflow='auto'
        >
          <MDBox display='flex' flexDirection='column' gap={1}>
            <MDBox display='flex' flexDirection='row' gap={1}>
              {subscriptionFlag &&
                (isSquirbyEnabledByPlan ? (
                  <FaIcon
                    icon='lock-open'
                    typographyProps={{
                      variant: 'xs',
                      color: 'primary',
                      mt: '8px'
                    }}
                  />
                ) : (
                  <FaIcon
                    icon='lock'
                    typographyProps={{
                      variant: 'xs',
                      color: 'primary',
                      mt: '8px'
                    }}
                  />
                ))}
              <FaIcon icon='sparkles' size='2xs' />
              <MDTypography variant='h4' mb={1} color='dark'>
                Ask Squirby!
              </MDTypography>
            </MDBox>
            <MDTypography variant='lg' color='info'>
              Type a question about your data or specify the chart you would
              like Squirby to create or customise for you, or ask for insights!
              😊
            </MDTypography>

            {botRequestData?.prompt && botRequestData?.message && (
              <Grid
                container
                display='flex'
                flexDirection='column'
                gap={2}
                width='100%'
                my={3}
              >
                <Grid
                  item
                  width='fit-content'
                  alignSelf='flex-end'
                  sx={({ palette }) => ({
                    ml: '10%',
                    border: `1px solid ${palette.grey[300]}`,
                    p: 1,
                    borderRadius: '5px',
                    backgroundColor: palette.light.main,
                    boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)',
                    maxWidth: '90%'
                  })}
                >
                  <Grid
                    container
                    display='flex'
                    flexDirection='row'
                    gap={1}
                    alignItems='center'
                    justifyContent='flex-end'
                    flexWrap='nowrap'
                  >
                    {/* <MDBox
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      sx={({ palette }) => ({
                        border: `1px solid rgba(35, 109, 169, 0.5)`,
                        borderRadius: 50,
                        boxShadow: '0 3px 10px rgb(0 0 0 / 0.2)',
                        height: 45,
                        width: 45,
                        minWidth: 45
                      })}
                    >
                      <FaIcon icon='user' size='lg' />
                    </MDBox> */}
                    <MDTypography variant='sm' color='dark'>
                      YOU:
                    </MDTypography>
                    <Tooltip
                      title={
                        botRequestData?.prompt.length > 86
                          ? botRequestData?.prompt
                          : ''
                      }
                      placement='right'
                      PopperProps={{ sx: { zIndex: 6000 } }}
                    >
                      <MDTypography
                        variant='sm'
                        color='dark'
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: containsWhiteSpace(botRequestData?.prompt)
                            ? 'normal'
                            : 'break-all'
                        }}
                      >
                        {botRequestData?.prompt}
                      </MDTypography>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid item maxWidth='90%'>
                  <Grid
                    container
                    width='fit-content'
                    maxHeight='250px'
                    display='flex'
                    flexDirection='column'
                    justifyContent='flex-start'
                    flexWrap='nowrap'
                    gap={1.5}
                    sx={({ palette }) => ({
                      p: 1,
                      borderRadius: '5px',
                      overflowY: 'auto',
                      border: `1px solid ${palette.primary.main}`,
                      px: 1,
                      backgroundColor: palette.light.main,
                      boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)'
                    })}
                  >
                    <Grid
                      container
                      flexWrap='nowrap'
                      display='flex'
                      flexDirection='row'
                      alignItems='center'
                    >
                      <MDBox
                        component='img'
                        src={brandLogo}
                        alt='Brand'
                        sx={{
                          height: 50,
                          width: 50,
                          minWidth: 50,
                          padding: '10px'
                        }}
                      />
                      <MDTypography
                        variant='sm'
                        color='dark'
                        sx={{
                          wordBreak: containsWhiteSpace(botRequestData?.prompt)
                            ? 'normal'
                            : 'break-all'
                        }}
                      >
                        {clickedSeeMore ? (
                          botRequestData?.message
                        ) : (
                          <>
                            {' '}
                            {botRequestData?.message.length > 130
                              ? botRequestData?.message.slice(0, 130) + '...'
                              : botRequestData?.message}
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                            <span
                              style={{
                                textDecoration: 'underline',
                                textUnderlineOffset: '3px',
                                cursor: 'pointer'
                              }}
                              onClick={() => setClickedSeeMore(true)}
                            >
                              {botRequestData?.message.length > 130
                                ? 'read more'
                                : ''}
                            </span>
                          </>
                        )}
                      </MDTypography>
                    </Grid>
                    {/* </MDBox> */}
                    <Grid
                      container
                      display='flex'
                      flexDirection='column'
                      gap={1}
                    >
                      {botRequestData?.sqb_id && (
                        <Grid display='flex' justifyContent='flex-end' gap={2}>
                          <Grid
                            container
                            display='flex'
                            flexDirection='row'
                            gap={1}
                            alignItems='center'
                            width='fit-content'
                          >
                            <Tooltip
                              title='Select if Squirby response was helpful!'
                              PopperProps={{ sx: { zIndex: 6000 } }}
                            >
                              <span>
                                <FaIcon
                                  icon={[
                                    botRequestData.rating === 1 ? 'fas' : 'far',
                                    'thumbs-up'
                                  ]}
                                  style={{
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleVoteClick(1)}
                                />
                              </span>
                            </Tooltip>

                            <Tooltip
                              title='Select if Squirby response was not satisfactory :('
                              PopperProps={{ sx: { zIndex: 6000 } }}
                            >
                              <span>
                                <FaIcon
                                  icon={[
                                    botRequestData.rating === -1
                                      ? 'fas'
                                      : 'far',
                                    'thumbs-down'
                                  ]}
                                  style={{
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleVoteClick(-1)}
                                />
                              </span>
                            </Tooltip>
                          </Grid>
                          {botRequestData?.button_text && !usedButton && (
                            <MDTypography
                              onClick={() =>
                                handleButtonClick(botRequestData?.action)
                              }
                              variant='h5'
                              color='dark'
                              underline
                              sx={{
                                textUnderlineOffset: 3,
                                cursor: 'pointer'
                              }}
                            >
                              {botRequestData?.button_text}
                            </MDTypography>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </MDBox>
          <MDBox width='100%' mt={2}>
            <MDInput
              variant='outlined'
              fullWidth
              multiline
              rows={2}
              inputRef={(input: any) => {
                if (botSearchText.length === 0) input?.focus();
              }}
              // sx={({ palette }: any) => ({
              //   background: linearGradient(
              //     palette.gradients.secondary.main,
              //     palette.gradients.secondary.state
              //   ),
              //   '& input::placeholder': {
              //     color: 'red' // Set your desired color here
              //   }
              // })}
              placeholder={ADD_CHART_PLACEHOLDER}
              onKeyDown={handleSquirrelBotReq}
              onClickEnter={() => {
                handleSquirrelBotReq();
              }}
              needEnterIcon
              onChange={
                !isSquirbyEnabledByPlan
                  ? handleSquriByLockedOnChange
                  : (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!botReqLoading) {
                        setBotSearchText(e.target.value);
                        dispatch(setSqBotData({ draftPrompt: e.target.value }));
                      }
                    }
              }
              value={botSearchText}
              // inputProps={{
              //   style: {
              //     color: 'white' // Set text color to white
              //   },
              //   sx: {
              //     textAlign: 'initial',
              //     '& input': {
              //       color: 'red'
              //     },
              //     '&::placeholder': {
              //       color: '#FFFFFF'
              //     }
              //   }
              // }}
              isLoading={botReqLoading}
              isSqBChatWidget
            />
            <MDBox
              display='flex'
              flexDirection='row'
              alignItems='center'
              gap={1}
            >
              <MDTypography variant='xs' color='info'>
                Squirby is an AI bot. Please check the answers once.
              </MDTypography>
              <Tooltip
                title='DataSquirrel does not send raw data to an LLM for security & data privacy reasons. We use LLM’s for assisted analysis. However, it is recommended that you verify any answers to ensure there are no unexpected / untrue outcomes. In AI those are called hallucinations.'
                PopperProps={{ sx: { zIndex: 6000 } }}
              >
                <span>
                  <FaIcon
                    icon='circle-info'
                    size='xs'
                    typographyProps={{
                      color: 'primary'
                    }}
                  />
                </span>
              </Tooltip>
            </MDBox>
          </MDBox>
        </MDBox>
      </>
    </Dialog>
  );
};

export default SqBChatWidget;
